import { useQueryClient } from '@tanstack/react-query';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';

// ── API response / request shapes ────────────────────────────────────────────

export interface EmailSettingsResponse {
  fromEmail: string;
  fromDisplayName: string;
  replyToEmail: string | null;
}

export interface UpsertEmailSettingsRequest {
  fromDisplayName: string;
  replyToEmail?: string | null;
}

// ── Demo / mock data ─────────────────────────────────────────────────────────

const MOCK_EMAIL_SETTINGS: EmailSettingsResponse = {
  fromEmail: 'noreply@example.com',
  fromDisplayName: 'noreply@example.com',
  replyToEmail: null,
};

// ── Query ────────────────────────────────────────────────────────────────────

export function useEmailSettings() {
  const { apiGet } = useApi();

  return useModeAwareQuery<EmailSettingsResponse, Error>(
    ['email-settings'],
    async ({ signal }) => {
      return apiGet<EmailSettingsResponse>('/api/v1/settings/email', undefined, signal);
    },
    MOCK_EMAIL_SETTINGS,
    {
      staleTime: 5 * 60 * 1000,
    },
  );
}

// ── Mutation ─────────────────────────────────────────────────────────────────

export function useUpsertEmailSettings() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<EmailSettingsResponse, Error, UpsertEmailSettingsRequest>(
    async (data) => {
      return apiPut<EmailSettingsResponse>('/api/v1/settings/email', data);
    },
    async (data) => {
      return {
        fromEmail: 'noreply@example.com',
        fromDisplayName: data.fromDisplayName,
        replyToEmail: data.replyToEmail ?? null,
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['email-settings'] });
      },
    },
  );
}
